/**
 * Web wrapper for react-native-popover-view.
 *
 * Every floating surface in the POS - confirmation dialogs, filter menus,
 * date pickers, the customer and product selectors, context menus - is a
 * <Popover>, and each of the ~37 call sites picks its own width inline:
 *
 *   popoverStyle={{ width: width > 600 ? 400 : 300, borderRadius: 10 }}
 *   popoverStyle={{ width: width > 900 ? 600 : isLandScape ? 450 : 350, ... }}
 *   popoverStyle={{ elevation: 4, bottom: 10 }}          <- no radius at all
 *
 * so no two popovers agree on width, radius or how they behave when the
 * browser window is small. This wrapper supplies the missing floor and
 * ceiling; webpack aliases the bare specifier to it for the web build only
 * (see webpack.config.js), so no call site changes and Android is untouched.
 *
 * Deliberately conservative: the defaults are merged *before* each call
 * site's popoverStyle, so an intentional 600px dialog stays 600px. What
 * changes is only what was unspecified - a minimum width, a consistent
 * radius, and a viewport-aware maximum so nothing can render off-screen when
 * the window is narrower than the hardcoded width.
 *
 * Height is left to the library: react-native-popover-view already measures
 * the display area and clamps content through `forcedContentSize`
 * (BasePopover.js), so adding a second maxHeight here would only fight it.
 */

import React from 'react';
import {StyleSheet, useWindowDimensions} from 'react-native';
/*
 * The `/dist/index` specifier is deliberate: it does not match the
 * `react-native-popover-view$` exact alias, so this import reaches the real
 * library instead of recursing into this file.
 */
import BasePopover from 'react-native-popover-view/dist/index';
import type {PublicPopoverProps} from 'react-native-popover-view/dist/Popover';

import {clampToViewport, webPopover} from './webTokens';

/* Named exports (PopoverPlacement, PopoverMode, Rect, Size) pass through. */
export * from 'react-native-popover-view/dist/index';

const styles = StyleSheet.create({
  /** One overlay tone behind every popover, matching the modal overlay. */
  backdrop: {backgroundColor: 'rgba(0, 0, 0, 0.35)'},
});

/**
 * Confirmation dialogs (CommonDialogBox) that were given no width.
 *
 * Most screens size their confirmation popover explicitly - the convention is
 * `width > 600 ? 400 : 300` - but nine do not (sort options, add/edit option,
 * option list, add/edit role, add/edit staff, logout, the order list's delete
 * and delete-filter dialogs, and the order details return prompt), and one
 * passes width: 'auto'. On web such a popover shrinks to its content, the
 * dialog's message (laid out at 80% of that) collapses into a narrow column,
 * and the card ends up ~240px wide with its buttons crowded. Android sizes the
 * same dialog from the screen, so it never shows there.
 *
 * Only a popover whose child is a CommonDialogBox, and whose call site set no
 * numeric width, gets the conventional width. Menus, filters and pickers keep
 * sizing to their content; every explicit width still wins.
 *
 * Exported for popoverDialog.web.tsx, which applies the same rule to the
 * screens that import 'react-native-popover-view/dist/Popover' directly.
 */
export function confirmationWidth(
  children: React.ReactNode,
  popoverStyle: PublicPopoverProps['popoverStyle'],
  viewportWidth: number,
): number | undefined {
  /*
   * Required lazily for the same reason as webZenInput's signInStyle: this
   * wrapper loads inside appTheme's import chain, and a top-level import of
   * CommonDialogBox (which imports appTheme) builds theme style sheets before
   * appColors exists.
   */
  const CommonDialogBox = require('../../shared/commonComponents/commonDialogBox').default;
  const onlyChild = React.Children.toArray(children)[0];
  if (!React.isValidElement(onlyChild) || onlyChild.type !== CommonDialogBox) {
    return undefined;
  }
  const requested = StyleSheet.flatten(popoverStyle)?.width;
  if (typeof requested === 'number' || (typeof requested === 'string' && requested !== 'auto')) {
    return undefined;
  }
  return viewportWidth > 600 ? 400 : 300;
}

const WebPopover = ({
  popoverStyle,
  backgroundStyle,
  children,
  ...props
}: PublicPopoverProps) => {
  const {width} = useWindowDimensions();
  const dialogWidth = confirmationWidth(children, popoverStyle, width);

  const defaults = {
    minWidth: webPopover.minWidth,
    maxWidth: clampToViewport(
      webPopover.maxWidth,
      width,
      webPopover.viewportInset,
    ),
    borderRadius: webPopover.borderRadius,
    /* So the radius actually clips long lists and tables inside. */
    overflow: 'hidden' as const,
  };

  return (
    <BasePopover
      {...props}
      /* dialogWidth last: it only exists when the call site gave no width. */
      popoverStyle={[defaults, popoverStyle, dialogWidth ? {width: dialogWidth} : null]}
      /* Call sites that pass their own backgroundStyle still win. */
      backgroundStyle={[styles.backdrop, backgroundStyle]}>
      {children}
    </BasePopover>
  );
};

export default WebPopover;
